import React, { useEffect, useState } from 'react';
import { programService, Program, ProgramRegistration, ProgramStatsSummary } from '../../../../firebase/programFirestoreService';
import { useUI } from '../../../../components/common/UIMessageProvider';
import { FaPrint, FaDownload, FaFilter, FaEye, FaUsers, FaCheckCircle, FaTimesCircle, FaChartBar, FaExclamationTriangle, FaMale, FaFemale, FaHeart, FaHeartbeat } from '../../../../utils/iconUtils';

type ReportStats = {
  total: number;
  pending: number;
  confirmed: number;
  cancelled: number;
  waitlisted: number;
  attended: number;
  male: number;
  female: number;
  married: number;
  single: number;
  widow: number;
  divorced: number;
  separated: number;
  active: number;
  sick: number;
  partially_sick: number;
  disabled: number;
  healthOther: number;
  christian: number;
  muslim: number;
  religionOther: number;
};

type PrintCardGroup = 'summary' | 'gender' | 'religion' | 'marital' | 'health';
type PrintCardId =
  | 'total'
  | 'confirmed'
  | 'pending'
  | 'waitlisted'
  | 'attended'
  | 'male'
  | 'female'
  | 'christian'
  | 'muslim'
  | 'religionOther'
  | 'married'
  | 'single'
  | 'widow'
  | 'active'
  | 'sick'
  | 'disabled';

type PrintCardOption = {
  id: PrintCardId;
  label: string;
  group: PrintCardGroup;
  colorClass: string;
  valueKey: keyof ReportStats;
};

const PRINT_CARD_GROUPS: PrintCardGroup[] = ['summary', 'gender', 'religion', 'marital', 'health'];

const PRINT_CARD_GROUP_LABELS: Record<PrintCardGroup, string> = {
  summary: 'Summary Cards',
  gender: 'Gender Cards',
  religion: 'Religion Cards',
  marital: 'Marital Cards',
  health: 'Health Cards'
};

const PRINT_CARD_OPTIONS: PrintCardOption[] = [
  { id: 'total', label: 'Total', group: 'summary', colorClass: 'blue', valueKey: 'total' },
  { id: 'confirmed', label: 'Confirmed', group: 'summary', colorClass: 'green', valueKey: 'confirmed' },
  { id: 'pending', label: 'Pending', group: 'summary', colorClass: 'yellow', valueKey: 'pending' },
  { id: 'waitlisted', label: 'Waitlisted', group: 'summary', colorClass: 'gray', valueKey: 'waitlisted' },
  { id: 'attended', label: 'Attended', group: 'summary', colorClass: 'purple', valueKey: 'attended' },
  { id: 'male', label: 'Male', group: 'gender', colorClass: 'blue', valueKey: 'male' },
  { id: 'female', label: 'Female', group: 'gender', colorClass: 'pink', valueKey: 'female' },
  { id: 'christian', label: 'Christian', group: 'religion', colorClass: 'blue', valueKey: 'christian' },
  { id: 'muslim', label: 'Muslim', group: 'religion', colorClass: 'teal', valueKey: 'muslim' },
  { id: 'religionOther', label: 'Other Rel.', group: 'religion', colorClass: 'gray', valueKey: 'religionOther' },
  { id: 'married', label: 'Married', group: 'marital', colorClass: 'indigo', valueKey: 'married' },
  { id: 'single', label: 'Single', group: 'marital', colorClass: 'teal', valueKey: 'single' },
  { id: 'widow', label: 'Widow', group: 'marital', colorClass: 'gray', valueKey: 'widow' },
  { id: 'active', label: 'Active', group: 'health', colorClass: 'emerald', valueKey: 'active' },
  { id: 'sick', label: 'Sick', group: 'health', colorClass: 'red', valueKey: 'sick' },
  { id: 'disabled', label: 'Disabled', group: 'health', colorClass: 'orange', valueKey: 'disabled' }
];

const DEFAULT_PRINT_CARD_IDS = PRINT_CARD_OPTIONS.map(card => card.id);
const REPORT_ROW_PREVIEW_LIMIT = 300;

const EMPTY_REPORT_STATS: ReportStats = {
  total: 0,
  pending: 0,
  confirmed: 0,
  cancelled: 0,
  waitlisted: 0,
  attended: 0,
  male: 0,
  female: 0,
  married: 0,
  single: 0,
  widow: 0,
  divorced: 0,
  separated: 0,
  active: 0,
  sick: 0,
  partially_sick: 0,
  disabled: 0,
  healthOther: 0,
  christian: 0,
  muslim: 0,
  religionOther: 0
};

const getSummaryCount = (summary: ProgramStatsSummary, mapName: keyof ProgramStatsSummary, key: string) => {
  const map = summary[mapName] as Record<string, number> | undefined;
  return Number(map?.[key] || 0);
};

const mapSummaryToReportStats = (summary: ProgramStatsSummary): ReportStats => ({
  total: Number(summary.totalRegistrations || 0),
  pending: Number(summary.pending ?? getSummaryCount(summary, 'statusCounts', 'pending')),
  confirmed: Number(summary.confirmed ?? getSummaryCount(summary, 'statusCounts', 'confirmed')),
  cancelled: Number(summary.cancelled ?? getSummaryCount(summary, 'statusCounts', 'cancelled')),
  waitlisted: Number(summary.waitlisted ?? getSummaryCount(summary, 'statusCounts', 'waitlisted')),
  attended: Number(summary.attended || 0),
  male: Number(summary.male ?? getSummaryCount(summary, 'genderCounts', 'male')),
  female: Number(summary.female ?? getSummaryCount(summary, 'genderCounts', 'female')),
  married: getSummaryCount(summary, 'maritalStatusCounts', 'married'),
  single: getSummaryCount(summary, 'maritalStatusCounts', 'single'),
  widow: getSummaryCount(summary, 'maritalStatusCounts', 'widow'),
  divorced: getSummaryCount(summary, 'maritalStatusCounts', 'divorced'),
  separated: getSummaryCount(summary, 'maritalStatusCounts', 'separated'),
  active: getSummaryCount(summary, 'healthStatusCounts', 'active'),
  sick: getSummaryCount(summary, 'healthStatusCounts', 'partially_sick'),
  partially_sick: getSummaryCount(summary, 'healthStatusCounts', 'partially_sick'),
  disabled: getSummaryCount(summary, 'healthStatusCounts', 'disabled'),
  healthOther: getSummaryCount(summary, 'healthStatusCounts', 'other'),
  christian: Number(summary.christian ?? getSummaryCount(summary, 'religionCounts', 'christian')),
  muslim: Number(summary.muslim ?? getSummaryCount(summary, 'religionCounts', 'muslim')),
  religionOther: Number(summary.otherReligion ?? getSummaryCount(summary, 'religionCounts', 'other'))
});

const combineReportStats = (items: ReportStats[]): ReportStats =>
  items.reduce((combined, item) => {
    (Object.keys(combined) as Array<keyof ReportStats>).forEach(key => {
      combined[key] += item[key];
    });
    return combined;
  }, { ...EMPTY_REPORT_STATS });

const mapRegistrationRow = (r: any, fallbackProgramId = '', fallbackProgramTitle = ''): ProgramRegistration => ({
  id: r.id,
  programId: r.programId || fallbackProgramId,
  programTitle: r.programTitle || fallbackProgramTitle,
  idNumber: r.idNumber || '',
  dateOfBirth: r.dateOfBirth || null,
  houseNumber: r.houseNumber || '',
  fullName: r.fullName,
  email: r.email,
  contactNumber: r.contactNumber,
  address: r.address,
  maritalStatus: r.maritalStatus || '',
  healthStatus: r.healthStatus || '',
  religion: r.religion || '',
  gender: r.gender || 'other',
  status: r.status || r.registrationStatus || 'pending',
  registrationDate: r.registrationDate,
  attended: r.attended || false
} as unknown as ProgramRegistration);

const ProgramReport: React.FC = () => {
  const [programs, setPrograms] = useState<Program[]>([]);
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [registrations, setRegistrations] = useState<ProgramRegistration[]>([]);
  const [statsFromSummary, setStatsFromSummary] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    gender: 'all',
    attended: 'all',
    marital: 'all',
    health: 'all'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [showPrintOptions, setShowPrintOptions] = useState(false);
  const [selectedPrintCardIds, setSelectedPrintCardIds] = useState<PrintCardId[]>(DEFAULT_PRINT_CARD_IDS);
  const pageSize = 20;
  const [stats, setStats] = useState<ReportStats>(EMPTY_REPORT_STATS);
  const ui = useUI();

  useEffect(() => {
    loadPrograms();
  }, []);

  useEffect(() => {
    if (!statsFromSummary) {
      calculateStats();
    }
  }, [registrations, statsFromSummary]);

  const formatHealth = (h?: string) => {
    const v = (h || '').toLowerCase();
    if (!v) return '-';
    const map: Record<string, string> = {
      'active': 'Active',
      'sick': 'Sick',
      'partially_sick': 'Partially Sick',
      'disabled': 'Disabled',
      'other': 'Other'
    };
    return map[v] || v.charAt(0).toUpperCase() + v.slice(1);
  };

  const formatMarital = (m?: string) => {
    const v = (m || '').toLowerCase();
    if (!v) return '-';
    const map: Record<string, string> = {
      'single': 'Single',
      'married': 'Married',
      'widow': 'Widow',
      'divorced': 'Divorced',
      'separated': 'Separated'
    };
    return map[v] || v.charAt(0).toUpperCase() + v.slice(1);
  };

  const formatReligion = (religion?: string) => {
    const v = (religion || '').toString().toLowerCase();
    if (!v) return '-';
    if (v === 'christian') return 'Christian';
    if (v === 'muslim') return 'Muslim';
    if (v === 'other') return 'Other';
    return v.charAt(0).toUpperCase() + v.slice(1);
  };

  const formatDateOfBirth = (dob: any): string => {
    if (!dob) return '-';
    try {
      if (typeof dob?.toDate === 'function') {
        return dob.toDate().toLocaleDateString();
      }
      if (dob instanceof Date) {
        return isNaN(dob.getTime()) ? '-' : dob.toLocaleDateString();
      }
      const parsed = new Date(dob);
      return isNaN(parsed.getTime()) ? '-' : parsed.toLocaleDateString();
    } catch (error) {
      return '-';
    }
  };

  const hasColumnData = (
    rows: ProgramRegistration[],
    getValue: (row: ProgramRegistration) => unknown
  ): boolean => {
    return rows.some(row => {
      const value = getValue(row);
      if (value === null || value === undefined) return false;
      if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        return normalized !== '' && normalized !== '-' && normalized !== 'n/a' && normalized !== 'na';
      }
      return true;
    });
  };

  const loadPrograms = async () => {
    try {
      const data = await programService.getPrograms(200);
      const loadedPrograms = data || [];
      setPrograms(loadedPrograms);
      await loadAllRegistrations(loadedPrograms);
    } catch (err) {
      console.error('Failed to load programs', err);
    }
  };

  const calculateStats = () => {
    const total = registrations.length;
    const pending = registrations.filter(r => r.status === 'pending').length;
    const confirmed = registrations.filter(r => r.status === 'confirmed').length;
    const cancelled = registrations.filter(r => r.status === 'cancelled').length;
    const waitlisted = registrations.filter(r => r.status === 'waitlisted').length;
    const attended = registrations.filter(r => r.attended).length;
    const male = registrations.filter(r => r.gender === 'male').length;
    const female = registrations.filter(r => r.gender === 'female').length;
    
    // Marital status - matching your form exactly
    const married = registrations.filter(r => ((r as any).maritalStatus || '').toLowerCase() === 'married').length;
    const single = registrations.filter(r => ((r as any).maritalStatus || '').toLowerCase() === 'single').length;
    const widow = registrations.filter(r => ((r as any).maritalStatus || '').toLowerCase() === 'widow').length;
    const divorced = registrations.filter(r => ((r as any).maritalStatus || '').toLowerCase() === 'divorced').length;
    const separated = registrations.filter(r => ((r as any).maritalStatus || '').toLowerCase() === 'separated').length;

    // Health status - matching your form exactly
    const active = registrations.filter(r => ((r as any).healthStatus || '').toLowerCase() === 'active').length;
    // The SICK card should count "partially_sick" entries per request
    const sick = registrations.filter(r => ((r as any).healthStatus || '').toLowerCase() === 'partially_sick').length;
    const partially_sick = registrations.filter(r => ((r as any).healthStatus || '').toLowerCase() === 'partially_sick').length;
    const disabled = registrations.filter(r => ((r as any).healthStatus || '').toLowerCase() === 'disabled').length;
    const healthOther = registrations.filter(r => ((r as any).healthStatus || '').toLowerCase() === 'other').length;

    // Religion status
    const christian = registrations.filter(r => ((r as any).religion || '').toLowerCase() === 'christian').length;
    const muslim = registrations.filter(r => ((r as any).religion || '').toLowerCase() === 'muslim').length;
    const religionOther = registrations.filter(r => {
      const religion = ((r as any).religion || '').toLowerCase();
      return religion === 'other' || (!!religion && religion !== 'christian' && religion !== 'muslim');
    }).length;
    
    setStats({ 
      total, pending, confirmed, cancelled, waitlisted, attended, 
      male, female, 
      married, single, widow, divorced, separated,
      active, sick, partially_sick, disabled, healthOther,
      christian, muslim, religionOther
    });
  };

  const loadRegistrationsForProgram = async (programId: string) => {
    setLoading(true);
    try {
      const [program, summary, regs] = await Promise.all([
        programService.getProgram(programId),
        programService.getProgramStatsSummary(programId),
        programService.getProgramRegistrations(programId, REPORT_ROW_PREVIEW_LIMIT)
      ]);
      setSelectedProgram(program);

      if (summary) {
        setStats(mapSummaryToReportStats(summary));
        setStatsFromSummary(true);
      } else {
        setStatsFromSummary(false);
      }

      setRegistrations((regs || []).map(r => mapRegistrationRow(r, programId, program?.title || '')));
    } catch (err) {
      console.error('Failed to load registrations', err);
      ui.showMessage('Failed to load registrations.', { type: 'error' });
      setRegistrations([]);
      setStatsFromSummary(false);
    } finally {
      setLoading(false);
    }
  };

  const onProgramChange = async (programId: string) => {
    if (!programId) {
      await loadAllRegistrations();
      return;
    }
    await loadRegistrationsForProgram(programId);
  };

  const loadAllRegistrations = async (programsOverride?: Program[]) => {
    setLoading(true);
    try {
      const programsList = programsOverride || programs || [];
      const summaries = await programService.getProgramStatsSummaries(200);
      const summaryStats = summaries.map(mapSummaryToReportStats);
      const recentRows = summaries
        .flatMap(summary => (summary.recentRegistrations || []).map(row => mapRegistrationRow(row, summary.programId, summary.programTitle || '')))
        .sort((a, b) => {
          const dateA = (a.registrationDate as any)?.toDate?.()?.getTime?.() || 0;
          const dateB = (b.registrationDate as any)?.toDate?.()?.getTime?.() || 0;
          return dateB - dateA;
        })
        .slice(0, REPORT_ROW_PREVIEW_LIMIT);

      if (programsList.length && summaries.length < programsList.length) {
        const missingProgramCount = programsList.length - summaries.length;
        console.warn(`${missingProgramCount} program(s) do not have report summaries yet. Run npm run backfill-program-stats.`);
      }

      setStats(summaryStats.length ? combineReportStats(summaryStats) : EMPTY_REPORT_STATS);
      setStatsFromSummary(true);
      setRegistrations(recentRows);
      setSelectedProgram(null);
    } catch (err) {
      console.error('Failed to load all registrations', err);
      setRegistrations([]);
      setStats(EMPTY_REPORT_STATS);
      setStatsFromSummary(true);
    } finally {
      setLoading(false);
    }
  };

  const loadFullRegistrationsForSelectedProgram = async () => {
    if (!selectedProgram?.id) return;

    setLoading(true);
    try {
      const regs = await programService.getProgramRegistrations(selectedProgram.id);
      setRegistrations((regs || []).map(r => mapRegistrationRow(r, selectedProgram.id!, selectedProgram.title || '')));
    } catch (err) {
      console.error('Failed to load full program registrations', err);
      ui.showMessage('Failed to load full program rows.', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const openPrintOptions = () => {
    setShowPrintOptions(true);
  };

  const selectAllPrintCards = () => {
    setSelectedPrintCardIds(DEFAULT_PRINT_CARD_IDS);
  };

  const clearPrintCards = () => {
    setSelectedPrintCardIds([]);
  };

  const togglePrintCard = (cardId: PrintCardId) => {
    setSelectedPrintCardIds(current =>
      current.includes(cardId)
        ? current.filter(id => id !== cardId)
        : [...current, cardId]
    );
  };

  const togglePrintCardGroup = (group: PrintCardGroup) => {
    const groupCardIds = PRINT_CARD_OPTIONS
      .filter(card => card.group === group)
      .map(card => card.id);

    setSelectedPrintCardIds(current => {
      const hasEveryCardInGroup = groupCardIds.every(id => current.includes(id));

      if (hasEveryCardInGroup) {
        return current.filter(id => !groupCardIds.includes(id));
      }

      return PRINT_CARD_OPTIONS
        .map(card => card.id)
        .filter(id => current.includes(id) || groupCardIds.includes(id));
    });
  };

  const escapePrintHtml = (value: unknown): string => {
    const raw = value === null || value === undefined ? '' : String(value);
    return raw.replace(/[&<>"']/g, char => {
      const entities: Record<string, string> = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      };
      return entities[char];
    });
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      ui.showMessage('Unable to open print window. Please allow pop-ups.', { type: 'error' });
      return;
    }

    const selectedCards = PRINT_CARD_OPTIONS.filter(card => selectedPrintCardIds.includes(card.id));
    const statsSectionHtml = selectedCards.length
      ? `
          <div class="stats-section">
            <div class="stats-grid">
              ${selectedCards.map(card => `
                <div class="stat-card ${card.colorClass}">
                  <div class="stat-label">${escapePrintHtml(card.label)}</div>
                  <div class="stat-value">${stats[card.valueKey]}</div>
                </div>
              `).join('')}
            </div>
          </div>
        `
      : '';

    const reportTitle = selectedProgram?.title || 'ALL PROGRAMS REPORT';
    const programName = selectedProgram?.title || 'All Programs';
    const generatedAt = new Date().toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Program Report - ${escapePrintHtml(programName)}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          :root { --accent: #486581; }
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 10px; background: #ffffff; min-height: 100vh; color: #111827; }
          
          .container {
            background: white;
            border-radius: 16px;
            box-shadow: 0 20px 60px rgba(0,0,0,0.3);
            overflow: hidden;
          }
          
          .header { background: var(--accent); color: #ffffff; padding: 17px 22px; text-align: center; position: relative; overflow: hidden; }
          
          .header::before {
            content: '';
            position: absolute;
            top: -50%;
            right: -50%;
            width: 200%;
            height: 200%;
            background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
            animation: pulse 4s ease-in-out infinite;
          }
          
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 0.5; }
            50% { transform: scale(1.1); opacity: 0.8; }
          }
          
          .header-content {
            position: relative;
            z-index: 1;
          }

          .foundation-name {
            font-size: 17px;
            font-weight: 600;
            margin-bottom: 9px;
            letter-spacing: 1px;
            opacity: 0.95;
          }
          
          .header h1 { 
            font-size: 29px;
            font-weight: 700;
            margin-bottom: 6px;
            text-transform: uppercase;
            letter-spacing: 1.6px;
            text-shadow: 2px 2px 4px rgba(0,0,0,0.2);
          }
          
          .header p { 
            font-size: 13px;
            opacity: 0.95;
            font-weight: 300;
            line-height: 1.25;
          }
          
          .stats-section {
            padding: 16px 18px;
            background: linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%);
          }
          
          .stats-grid { 
            display: grid; 
            grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
            gap: 10px;
            margin-bottom: 10px;
          }
          
          .stat-card { background: white; border-radius: 8px; padding: 9px 8px; text-align: center; box-shadow: 0 2px 8px rgba(0,0,0,0.06); border-left: 3px solid var(--accent); transition: transform 0.2s; position: relative; overflow: hidden; color: var(--accent); }
          
          .stat-card::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 3px;
            background: linear-gradient(90deg, transparent, currentColor, transparent);
            opacity: 0.3;
          }
          
          /* unified accent for print */
          .stat-card.blue, .stat-card.green, .stat-card.yellow, .stat-card.purple, .stat-card.pink, .stat-card.indigo, .stat-card.teal, .stat-card.emerald, .stat-card.red, .stat-card.orange, .stat-card.gray { border-left-color: var(--accent); color: var(--accent); }
          
          .stat-label {
            font-size: 9px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            opacity: 0.8;
            margin-bottom: 4px;
          }
          
          .stat-value {
            font-size: 18px;
            font-weight: 700;
            line-height: 1;
          }
          
          .table-container {
            padding: 0 12px 12px;
          }
          
          table { 
            width: 100%; 
            border-collapse: separate;
            border-spacing: 0;
            font-size: 11px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.05);
            border-radius: 8px;
            overflow: hidden;
          }
          
          thead { background: var(--accent); color: #ffffff; }
          
          th { 
            padding: 10px 8px;
            text-align: left;
            font-weight: 600;
            text-transform: uppercase;
            font-size: 10px;
            letter-spacing: 0.5px;
            border-bottom: 2px solid rgba(255,255,255,0.2);
            white-space: nowrap;
          }
          
          tbody tr {
            background: white;
            transition: background 0.2s;
          }
          
          tbody tr:nth-child(even) {
            background: #f9fafb;
          }
          
          tbody tr:hover {
            background: #f3f4f6;
          }
          
          td { 
            padding: 10px;
            border-bottom: 1px solid #e5e7eb;
            white-space: nowrap;
          }
          
          tbody tr:last-child td {
            border-bottom: none;
          }
          
          .badge {
            display: inline-block;
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 9px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .badge { background: rgba(72,101,129,0.08); color: var(--accent); }
          
          .check-icon {
            color: #10b981;
            font-weight: bold;
            font-size: 14px;
          }
          
          .cross-icon {
            color: #d1d5db;
            font-weight: bold;
            font-size: 14px;
          }
          
          .footer {
            background: linear-gradient(to right, #f9fafb, #f3f4f6);
            padding: 15px 25px;
            border-top: 2px solid #e5e7eb;
            display: flex;
            justify-content: space-between;
            align-items: center;
            font-size: 11px;
            color: #6b7280;
            flex-wrap: wrap;
            gap: 10px;
          }
          
          .footer-left {
            display: flex;
            gap: 20px;
            align-items: center;
            flex-wrap: wrap;
          }

          .copyright {
            font-size: 10px;
            color: #9ca3af;
            font-style: italic;
            margin-left: auto;
          }
          
          .footer-item {
            display: flex;
            align-items: center;
            gap: 5px;
          }
          
          .footer-label {
            font-weight: 600;
            color: #374151;
          }
          
          @media print {
            @page {
              margin: 5mm;
            }

            body { 
              padding: 0;
              margin: 0;
              background: white;
            }
            
            .container {
              box-shadow: none;
            }

            .table-container {
              padding: 0 4px 8px;
            }

            .header {
              padding: 10px 12px;
            }

            .foundation-name {
              font-size: 12px;
              margin-bottom: 3px;
              letter-spacing: 0.5px;
            }

            .header h1 {
              font-size: 20px;
              margin-bottom: 2px;
              letter-spacing: 0.8px;
              text-shadow: none;
            }

            .header p {
              font-size: 10px;
              line-height: 1.2;
            }

            th, td {
              padding: 6px 5px;
              font-size: 9px;
            }
            
            .stat-card {
              break-inside: avoid;
              padding: 7px 6px;
              border-radius: 6px;
            }

            .stats-section {
              padding: 10px 12px;
            }

            .stats-grid {
              grid-template-columns: repeat(4, minmax(0, 1fr));
              gap: 6px;
              margin-bottom: 8px;
            }

            .stat-label {
              font-size: 8px;
              margin-bottom: 2px;
            }

            .stat-value {
              font-size: 15px;
            }
            
            tbody tr {
              break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <div class="header-content">
              <div class="foundation-name">ALHAJI INUSAH FOUNDATION</div>
              <h1>${escapePrintHtml(reportTitle)}</h1>
              <p>Generated on ${escapePrintHtml(generatedAt)}</p>
            </div>
          </div>
          
          ${statsSectionHtml}
          
          <div class="table-container">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Contact</th>
                  ${showGhanaCardColumn ? '<th>Ghana Card</th>' : ''}
                  ${showHouseNumberColumn ? '<th>House No</th>' : ''}
                  <th>Address</th>
                  ${showDateOfBirthColumn ? '<th>Date of Birth</th>' : ''}
                  ${showMaritalColumn ? '<th>Marital</th>' : ''}
                  ${showHealthColumn ? '<th>Health</th>' : ''}
                  <th>Gender</th>
                  ${showReligionColumn ? '<th>Religion</th>' : ''}
                  <th>Status</th>
                  <th>Attended</th>
                </tr>
              </thead>
              <tbody>
                ${filteredRegistrations.map((r, i) => `
                  <tr>
                    <td><strong>${i + 1}</strong></td>
                    <td style="font-weight: 600; color: #111827;">${escapePrintHtml(r.fullName || '-')}</td>
                    <td>${escapePrintHtml(r.contactNumber || '-')}</td>
                    ${showGhanaCardColumn ? `<td>${escapePrintHtml((r as any).idNumber || '-')}</td>` : ''}
                    ${showHouseNumberColumn ? `<td>${escapePrintHtml((r as any).houseNumber || '-')}</td>` : ''}
                    <td style="max-width: 180px;">${escapePrintHtml(r.address || '-')}</td>
                    ${showDateOfBirthColumn ? `<td>${escapePrintHtml(formatDateOfBirth((r as any).dateOfBirth))}</td>` : ''}
                    ${showMaritalColumn ? `<td>${escapePrintHtml(formatMarital((r as any).maritalStatus))}</td>` : ''}
                    ${showHealthColumn ? `<td>${escapePrintHtml(formatHealth((r as any).healthStatus))}</td>` : ''}
                    <td>
                      <span class="badge">${escapePrintHtml((r.gender || '-').toUpperCase())}</span>
                    </td>
                    ${showReligionColumn ? `<td>${escapePrintHtml(formatReligion((r as any).religion))}</td>` : ''}
                    <td>
                      <span class="badge">${escapePrintHtml((r.status || '-').toUpperCase())}</span>
                    </td>
                    <td style="text-align: center;">
                      <span class="${r.attended ? 'check-icon' : 'cross-icon'}">${r.attended ? '&#10003;' : '&#10007;'}</span>
                    </td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
          
          <div class="footer">
            <div class="footer-left">
              <div class="footer-item">
                <span class="footer-label">Total Records:</span>
                <span>${filteredRegistrations.length}</span>
              </div>
              <div class="footer-item">
                <span class="footer-label">Program:</span>
                <span>${escapePrintHtml(programName)}</span>
              </div>
            </div>
            <div class="copyright">&copy; ${new Date().getFullYear()} Alhaji Inusah Foundation. All rights reserved.</div>
          </div>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    setShowPrintOptions(false);
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const exportToCSV = () => {
    const escapeCsv = (value: unknown): string => {
      const raw = value === null || value === undefined ? '' : String(value);
      const escaped = raw.replace(/"/g, '""');
      return `"${escaped}"`;
    };

    const headers = ['No.', 'Name', 'Contact'];
    if (showGhanaCardColumn) headers.push('Ghana Card');
    if (showHouseNumberColumn) headers.push('House No');
    headers.push('Address');
    if (showDateOfBirthColumn) headers.push('Date of Birth');
    if (showMaritalColumn) headers.push('Marital');
    if (showHealthColumn) headers.push('Health');
    headers.push('Gender');
    if (showReligionColumn) headers.push('Religion');
    headers.push('Status', 'Attended');

    const csvData = filteredRegistrations.map((r, i) => {
      const row: string[] = [
        escapeCsv(i + 1),
        escapeCsv(r.fullName || '-'),
        escapeCsv(r.contactNumber || '-')
      ];
      if (showGhanaCardColumn) row.push(escapeCsv((r as any).idNumber || '-'));
      if (showHouseNumberColumn) row.push(escapeCsv((r as any).houseNumber || '-'));
      row.push(escapeCsv(r.address || '-'));
      if (showDateOfBirthColumn) row.push(escapeCsv(formatDateOfBirth((r as any).dateOfBirth)));
      if (showMaritalColumn) row.push(escapeCsv(formatMarital((r as any).maritalStatus)));
      if (showHealthColumn) row.push(escapeCsv(formatHealth((r as any).healthStatus)));
      row.push(escapeCsv((r.gender || '-').toUpperCase()));
      if (showReligionColumn) row.push(escapeCsv(formatReligion((r as any).religion)));
      row.push(escapeCsv((r.status || '-').toUpperCase()), escapeCsv(r.attended ? 'Yes' : 'No'));
      return row;
    });

    const statsRows = [
      ['TOTAL', stats.total],
      ['CONFIRMED', stats.confirmed],
      ['PENDING', stats.pending],
      ['WAITLISTED', stats.waitlisted],
      ['ATTENDED', stats.attended],
      ['MALE', stats.male],
      ['FEMALE', stats.female],
      ['MARRIED', stats.married],
      ['SINGLE', stats.single],
      ['WIDOW', stats.widow],
      ['ACTIVE', stats.active],
      ['SICK', stats.sick],
      ['DISABLED', stats.disabled],
      ['CHRISTIAN', stats.christian],
      ['MUSLIM', stats.muslim],
      ['OTHER RELIGION', stats.religionOther]
    ];

    const csvContent = [
      `${escapeCsv('Program Report')},${escapeCsv(selectedProgram?.title || 'All Programs')}`,
      `${escapeCsv('Generated')},${escapeCsv(new Date().toLocaleString())}`,
      `${escapeCsv('Total Records')},${escapeCsv(filteredRegistrations.length)}`,
      '',
      escapeCsv('Statistics'),
      `${escapeCsv('Metric')},${escapeCsv('Value')}`,
      ...statsRows.map(([label, value]) => `${escapeCsv(label)},${escapeCsv(value)}`),
      '',
      headers.map(escapeCsv).join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `report-${selectedProgram?.title?.replace(/\s+/g, '-') || 'all'}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredRegistrations = registrations.filter(reg => {
    if (filters.status !== 'all' && reg.status !== filters.status) return false;
    if (filters.gender !== 'all' && reg.gender !== filters.gender) return false;
    if (filters.attended !== 'all') {
      if (filters.attended === 'yes' && !reg.attended) return false;
      if (filters.attended === 'no' && reg.attended) return false;
    }
    if (filters.marital !== 'all' && ((reg as any).maritalStatus || '').toLowerCase() !== filters.marital) return false;
    if (filters.health !== 'all' && ((reg as any).healthStatus || '').toLowerCase() !== filters.health) return false;
    return true;
  });

  const showGhanaCardColumn = hasColumnData(registrations, r => (r as any).idNumber);

  const showDateOfBirthColumn = registrations.some(r => formatDateOfBirth((r as any).dateOfBirth) !== '-');

  const showHouseNumberColumn = hasColumnData(registrations, r => (r as any).houseNumber);

  const showReligionColumn = hasColumnData(registrations, r => (r as any).religion);

  const showRegistrationDateColumn = registrations.some(r => !!(r as any).registrationDate);

  const canShowMaritalHealthColumns = selectedProgram
    ? !selectedProgram.requireDateOfBirth
    : true;
  const showMaritalColumn = canShowMaritalHealthColumns && hasColumnData(registrations, r => (r as any).maritalStatus);
  const showHealthColumn = canShowMaritalHealthColumns && hasColumnData(registrations, r => (r as any).healthStatus);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, registrations, selectedProgram]);

  const totalPages = Math.max(1, Math.ceil(filteredRegistrations.length / pageSize));
  const paginatedRegistrations = filteredRegistrations.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const showingStart = filteredRegistrations.length ? (currentPage - 1) * pageSize + 1 : 0;
  const showingEnd = Math.min(currentPage * pageSize, filteredRegistrations.length);
  const selectedPrintCardIdSet = new Set(selectedPrintCardIds);
  const canLoadFullSelectedProgramRows = Boolean(selectedProgram?.id && stats.total > registrations.length);
  const showingRecentSummaryRows = !selectedProgram && statsFromSummary && stats.total > registrations.length;

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200">
      {/* Compact Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white p-3">
        <div className="flex items-center justify-between">
          <h1 className="text-base text-white font-bold">Program Report</h1>
          <div className="flex gap-1.5">
            <button onClick={exportToCSV} disabled={!registrations.length && !stats.total} className="px-2 py-1 bg-white text-primary-700 rounded text-xs font-medium hover:bg-gray-100 disabled:opacity-50">
              <FaDownload className="inline h-3 w-3 mr-1" /> CSV
            </button>
            <button onClick={openPrintOptions} disabled={!registrations.length && !stats.total} className="px-2 py-1 bg-white text-primary-700 rounded text-xs font-medium hover:bg-gray-100 disabled:opacity-50">
              <FaPrint className="inline h-3 w-3 mr-1" /> Print
            </button>
          </div>
        </div>
      </div>

      {/* Ultra Compact Filters */}
      <div className="p-2 bg-gray-50 border-b">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          <select value={selectedProgram?.id || ''} onChange={(e) => onProgramChange(e.target.value)} className="px-2 py-1 border rounded text-xs">
            <option value="">All Programs</option>
            {programs.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
          
          <select value={filters.status} onChange={(e) => setFilters({...filters, status: e.target.value})} className="px-2 py-1 border rounded text-xs">
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="cancelled">Cancelled</option>
            <option value="waitlisted">Waitlisted</option>
          </select>

          <select value={filters.gender} onChange={(e) => setFilters({...filters, gender: e.target.value})} className="px-2 py-1 border rounded text-xs">
            <option value="all">All Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>

          <select value={filters.attended} onChange={(e) => setFilters({...filters, attended: e.target.value})} className="px-2 py-1 border rounded text-xs">
            <option value="all">All Attendance</option>
            <option value="yes">Attended</option>
            <option value="no">Not Attended</option>
          </select>

          <select value={filters.marital} onChange={(e) => setFilters({...filters, marital: e.target.value})} className="px-2 py-1 border rounded text-xs">
            <option value="all">All Marital</option>
            <option value="single">Single</option>
            <option value="married">Married</option>
            <option value="widow">Widow</option>
            <option value="divorced">Divorced</option>
            <option value="separated">Separated</option>
          </select>

          <select value={filters.health} onChange={(e) => setFilters({...filters, health: e.target.value})} className="px-2 py-1 border rounded text-xs">
            <option value="all">All Health</option>
            <option value="active">Active</option>
            <option value="sick">Sick</option>
            <option value="partially_sick">Partially Sick</option>
            <option value="disabled">Disabled</option>
            <option value="other">Other</option>
          </select>
        </div>
      </div>

      {/* Ultra Compact Stats Grid */}
      <div className="p-2 bg-white border-b">
        <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-1.5">
          <div className="bg-blue-50 border border-blue-200 rounded px-1.5 py-1 text-center">
            <div className="text-[9px] text-blue-600 font-medium">TOTAL</div>
            <div className="text-sm font-bold text-blue-700">{stats.total}</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded px-1.5 py-1 text-center">
            <div className="text-[9px] text-green-600 font-medium">CONFIRM</div>
            <div className="text-sm font-bold text-green-700">{stats.confirmed}</div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded px-1.5 py-1 text-center">
            <div className="text-[9px] text-yellow-600 font-medium">PENDING</div>
            <div className="text-sm font-bold text-yellow-700">{stats.pending}</div>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded px-1.5 py-1 text-center">
            <div className="text-[9px] text-gray-600 font-medium">WAITLIST</div>
            <div className="text-sm font-bold text-gray-700">{stats.waitlisted}</div>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded px-1.5 py-1 text-center">
            <div className="text-[9px] text-purple-600 font-medium">ATTEND</div>
            <div className="text-sm font-bold text-purple-700">{stats.attended}</div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded px-1.5 py-1 text-center">
            <div className="text-[9px] text-blue-600 font-medium">MALE</div>
            <div className="text-sm font-bold text-blue-700">{stats.male}</div>
          </div>
          <div className="bg-pink-50 border border-pink-200 rounded px-1.5 py-1 text-center">
            <div className="text-[9px] text-pink-600 font-medium">FEMALE</div>
            <div className="text-sm font-bold text-pink-700">{stats.female}</div>
          </div>
          <div className="bg-indigo-50 border border-indigo-200 rounded px-1.5 py-1 text-center">
            <div className="text-[9px] text-indigo-600 font-medium">CHRISTIAN</div>
            <div className="text-sm font-bold text-indigo-700">{stats.christian}</div>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded px-1.5 py-1 text-center">
            <div className="text-[9px] text-emerald-600 font-medium">MUSLIM</div>
            <div className="text-sm font-bold text-emerald-700">{stats.muslim}</div>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded px-1.5 py-1 text-center">
            <div className="text-[9px] text-gray-600 font-medium">OTHER REL</div>
            <div className="text-sm font-bold text-gray-700">{stats.religionOther}</div>
          </div>
          
          {/* Marital Status Cards */}
          <div className="bg-indigo-50 border border-indigo-200 rounded px-1.5 py-1 text-center">
            <div className="text-[9px] text-indigo-600 font-medium">MARRIED</div>
            <div className="text-sm font-bold text-indigo-700">{stats.married}</div>
          </div>
          <div className="bg-teal-50 border border-teal-200 rounded px-1.5 py-1 text-center">
            <div className="text-[9px] text-teal-600 font-medium">SINGLE</div>
            <div className="text-sm font-bold text-teal-700">{stats.single}</div>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded px-1.5 py-1 text-center">
            <div className="text-[9px] text-gray-600 font-medium">WIDOW</div>
            <div className="text-sm font-bold text-gray-700">{stats.widow}</div>
          </div>
          
          {/* Health Status Cards */}
          <div className="bg-emerald-50 border border-emerald-200 rounded px-1.5 py-1 text-center">
            <div className="text-[9px] text-emerald-600 font-medium">ACTIVE</div>
            <div className="text-sm font-bold text-emerald-700">{stats.active}</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded px-1.5 py-1 text-center">
            <div className="text-[9px] text-red-600 font-medium">SICK</div>
            <div className="text-sm font-bold text-red-700">{stats.sick}</div>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded px-1.5 py-1 text-center">
            <div className="text-[9px] text-orange-600 font-medium">DISABLED</div>
            <div className="text-sm font-bold text-orange-700">{stats.disabled}</div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex justify-center p-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          </div>
        ) : !filteredRegistrations.length ? (
          <div className="text-center py-8">
            <FaFilter className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-600">No registrations found</p>
            {canLoadFullSelectedProgramRows && (
              <button onClick={loadFullRegistrationsForSelectedProgram} className="mt-2 px-3 py-1 bg-primary-600 text-white rounded text-xs">
                Load All Rows
              </button>
            )}
          </div>
        ) : (
          <table className="min-w-full text-xs whitespace-nowrap">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-1 text-left text-[10px] font-semibold text-gray-700">#</th>
                <th className="px-2 py-1 text-left text-[10px] font-semibold text-gray-700">NAME</th>
                <th className="px-2 py-1 text-left text-[10px] font-semibold text-gray-700">CONTACT</th>
                {showGhanaCardColumn && (
                  <th className="px-2 py-1 text-left text-[10px] font-semibold text-gray-700">GHANA CARD</th>
                )}
                {showHouseNumberColumn && (
                  <th className="px-2 py-1 text-left text-[10px] font-semibold text-gray-700">HOUSE NO</th>
                )}
                <th className="px-2 py-1 text-left text-[10px] font-semibold text-gray-700">ADDRESS</th>
                {showDateOfBirthColumn && (
                  <th className="px-2 py-1 text-left text-[10px] font-semibold text-gray-700">DATE OF BIRTH</th>
                )}
                {showMaritalColumn && (
                  <th className="px-2 py-1 text-left text-[10px] font-semibold text-gray-700">MARITAL</th>
                )}
                {showHealthColumn && (
                  <th className="px-2 py-1 text-left text-[10px] font-semibold text-gray-700">HEALTH</th>
                )}
                <th className="px-2 py-1 text-left text-[10px] font-semibold text-gray-700">GENDER</th>
                {showReligionColumn && (
                  <th className="px-2 py-1 text-left text-[10px] font-semibold text-gray-700">RELIGION</th>
                )}
                <th className="px-2 py-1 text-left text-[10px] font-semibold text-gray-700">STATUS</th>
                {showRegistrationDateColumn && (
                  <th className="px-2 py-1 text-left text-[10px] font-semibold text-gray-700">DATE</th>
                )}
                <th className="px-2 py-1 text-center text-[10px] font-semibold text-gray-700">ATTEND</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedRegistrations.map((r, i) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-2 py-1 text-gray-600">{(currentPage - 1) * pageSize + i + 1}</td>
                  <td className="px-2 py-1 font-medium text-gray-900">{r.fullName}</td>
                  <td className="px-2 py-1 text-gray-600">{r.contactNumber || '-'}</td>
                  {showGhanaCardColumn && (
                    <td className="px-2 py-1 text-gray-900 font-mono">{(r as any).idNumber || '-'}</td>
                  )}
                  {showHouseNumberColumn && (
                    <td className="px-2 py-1 text-gray-600">{(r as any).houseNumber || '-'}</td>
                  )}
                  <td className="px-2 py-1 text-gray-600 max-w-[150px] truncate">{r.address || '-'}</td>
                  {showDateOfBirthColumn && (
                    <td className="px-2 py-1 text-gray-600">{formatDateOfBirth((r as any).dateOfBirth)}</td>
                  )}
                  {showMaritalColumn && (
                    <td className="px-2 py-1 text-gray-600">{formatMarital((r as any).maritalStatus)}</td>
                  )}
                  {showHealthColumn && (
                    <td className="px-2 py-1 text-gray-600">{formatHealth((r as any).healthStatus)}</td>
                  )}
                  <td className="px-2 py-1">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${
                      r.gender === 'male' ? 'bg-blue-100 text-blue-800' : 'bg-pink-100 text-pink-800'
                    }`}>
                      {r.gender.toUpperCase()}
                    </span>
                  </td>
                  {showReligionColumn && (
                    <td className="px-2 py-1 text-gray-600">{formatReligion((r as any).religion)}</td>
                  )}
                  <td className="px-2 py-1">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${
                      r.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                      r.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {r.status.toUpperCase()}
                    </span>
                  </td>
                  {showRegistrationDateColumn && (
                    <td className="px-2 py-1 text-gray-600">
                      {r.registrationDate ? new Date(r.registrationDate.toDate()).toLocaleDateString() : '-'}
                    </td>
                  )}
                  <td className="px-2 py-1 text-center">
                    {r.attended ? 
                      <FaCheckCircle className="inline h-3.5 w-3.5 text-green-600" /> : 
                      <FaTimesCircle className="inline h-3.5 w-3.5 text-gray-400" />
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Compact Footer */}
      <div className="px-3 py-2 bg-gray-50 border-t flex items-center justify-between text-[10px] text-gray-600">
        <div>
          Showing <strong>{showingStart}{showingEnd ? `-${showingEnd}` : ''}</strong> of <strong>{filteredRegistrations.length}</strong>
          {showingRecentSummaryRows && (
            <span className="ml-2 text-gray-500">Recent rows only; totals use saved summaries.</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canLoadFullSelectedProgramRows && (
            <button onClick={loadFullRegistrationsForSelectedProgram} className="px-2 py-1 bg-white border rounded text-xs text-primary-700">
              Load All Rows
            </button>
          )}
          <div className="flex items-center gap-2">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-2 py-1 bg-white border rounded text-xs disabled:opacity-50">Prev</button>
            <span className="text-[11px]">Page <strong>{currentPage}</strong> / {totalPages}</span>
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-2 py-1 bg-white border rounded text-xs disabled:opacity-50">Next</button>
          </div>
          <span>{new Date().toLocaleDateString()}</span>
        </div>
      </div>

      {showPrintOptions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b flex items-start justify-between gap-4">
              <div>
                <h2 className="text-sm font-semibold text-gray-900">Choose Print Cards</h2>
                <p className="text-xs text-gray-500 mt-0.5">Select the statistic cards to include in this printed report.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowPrintOptions(false)}
                className="text-gray-400 hover:text-gray-700"
                aria-label="Close print options"
              >
                <FaTimesCircle className="h-4 w-4" />
              </button>
            </div>

            <div className="px-4 py-2 border-b bg-gray-50 flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs text-gray-600">
                <strong>{selectedPrintCardIds.length}</strong> of <strong>{PRINT_CARD_OPTIONS.length}</strong> cards selected
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={selectAllPrintCards}
                  className="px-2.5 py-1 bg-white border border-gray-300 rounded text-xs font-medium text-gray-700 hover:bg-gray-100"
                >
                  All cards
                </button>
                <button
                  type="button"
                  onClick={clearPrintCards}
                  className="px-2.5 py-1 bg-white border border-gray-300 rounded text-xs font-medium text-gray-700 hover:bg-gray-100"
                >
                  Clear
                </button>
              </div>
            </div>

            <div className="p-4 grid gap-3 md:grid-cols-2 max-h-[60vh] overflow-y-auto">
              {PRINT_CARD_GROUPS.map(group => {
                const groupCards = PRINT_CARD_OPTIONS.filter(card => card.group === group);
                const isGroupSelected = groupCards.every(card => selectedPrintCardIdSet.has(card.id));

                return (
                  <div key={group} className="border border-gray-200 rounded-lg p-3">
                    <label className="flex items-center gap-2 text-xs font-semibold text-gray-800 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isGroupSelected}
                        onChange={() => togglePrintCardGroup(group)}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      {PRINT_CARD_GROUP_LABELS[group]}
                    </label>

                    <div className="mt-3 grid grid-cols-2 gap-2">
                      {groupCards.map(card => (
                        <label key={card.id} className="flex items-center gap-2 rounded border border-gray-100 bg-gray-50 px-2 py-1.5 text-xs cursor-pointer hover:bg-gray-100">
                          <input
                            type="checkbox"
                            checked={selectedPrintCardIdSet.has(card.id)}
                            onChange={() => togglePrintCard(card.id)}
                            className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-gray-700">{card.label}</span>
                          <span className="ml-auto font-semibold text-gray-900">{stats[card.valueKey]}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowPrintOptions(false)}
                className="px-3 py-1.5 bg-white border border-gray-300 rounded text-xs font-medium text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePrint}
                className="px-3 py-1.5 bg-primary-600 text-white rounded text-xs font-medium hover:bg-primary-700 inline-flex items-center gap-1.5"
              >
                <FaPrint className="h-3 w-3" />
                Print Report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgramReport;
